import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { apiFetch } from './apiBase';

const BREAKDOWN_SIDE_BY_METRIC = {
  onlyOracle: 'oracle',
  onlyAdp: 'adp',
  oraclePersons: 'oracle',
  inBoth: 'both',
};

function fmtMoney(n) {
  if (n == null || n === '') return '—';
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtHours(n) {
  if (n == null || n === '') return '—';
  const x = Number(n);
  if (!Number.isFinite(x) || x === 0) return '—';
  return x.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function OracleBreakdownTable({ rows }) {
  if (!rows?.length) {
    return <p className="py-2 text-xs text-slate-500">No Oracle element rows for this person.</p>;
  }
  return (
    <table className="data-table w-full text-xs">
      <thead>
        <tr>
          <th className="px-2 py-1.5 text-left font-medium">Oracle element</th>
          <th className="px-2 py-1.5 text-right font-medium">Amount</th>
          <th className="px-2 py-1.5 text-right font-medium">Lines</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.oracleElementName}>
            <td className="px-2 py-1.5 text-slate-200">{row.oracleElementName}</td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-300">
              {fmtMoney(row.amount)}
            </td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-500">
              {row.lineCount}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AdpBreakdownTable({ rows }) {
  if (!rows?.length) {
    return <p className="py-2 text-xs text-slate-500">No ADP register rows for this person.</p>;
  }
  return (
    <table className="data-table w-full text-xs">
      <thead>
        <tr>
          <th className="px-2 py-1.5 text-left font-medium">Wage type</th>
          <th className="px-2 py-1.5 text-left font-medium">Description</th>
          <th className="px-2 py-1.5 text-right font-medium">Amount</th>
          <th className="px-2 py-1.5 text-right font-medium">Hours</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.wageType}-${row.wageTypeLongText}`}>
            <td className="px-2 py-1.5 font-mono text-slate-200">{row.wageType}</td>
            <td className="max-w-[12rem] truncate px-2 py-1.5 text-slate-400" title={row.wageTypeLongText}>
              {row.wageTypeLongText || '—'}
            </td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-300">
              {fmtMoney(row.amount)}
            </td>
            <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-500">
              {fmtHours(row.hours)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PersonBreakdownPanel({ data, breakdownSide }) {
  if (!data) return null;
  if (breakdownSide === 'both' || data.side === 'both') {
    return (
      <div className="space-y-4">
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-cyan-500/90">
            Oracle elements
          </p>
          <OracleBreakdownTable rows={data.oracle} />
        </div>
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-cyan-500/90">
            ADP wage types
          </p>
          <AdpBreakdownTable rows={data.adp} />
        </div>
      </div>
    );
  }
  if (data.side === 'oracle' || breakdownSide === 'oracle') {
    return <OracleBreakdownTable rows={data.rows} />;
  }
  return <AdpBreakdownTable rows={data.rows} />;
}

/**
 * Person-list drill-down with column filters; click a person to see elements or wage types.
 */
export default function PersonDetailTable({
  rows,
  metric = '',
  filters,
  onFilterChange,
  onApply,
  onClearFilters,
  hasActiveFilters,
  hasPendingApply,
  loading,
  emptyMessage = 'No persons match the current filters.',
}) {
  const [expandedPerson, setExpandedPerson] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState('');

  const breakdownSide = BREAKDOWN_SIDE_BY_METRIC[metric] || 'oracle';

  useEffect(() => {
    setExpandedPerson(null);
    setBreakdown(null);
    setBreakdownError('');
  }, [rows, metric]);

  const set = (key, value) => onFilterChange(key, value);

  const togglePerson = async (personNumber) => {
    if (expandedPerson === personNumber) {
      setExpandedPerson(null);
      setBreakdown(null);
      setBreakdownError('');
      return;
    }
    setExpandedPerson(personNumber);
    setBreakdown(null);
    setBreakdownError('');
    setBreakdownLoading(true);
    try {
      const params = new URLSearchParams({
        person: personNumber,
        side: breakdownSide,
      });
      const data = await apiFetch(`/api/recon/person-breakdown?${params}`);
      setBreakdown(data);
    } catch (e) {
      setBreakdownError(e.message || 'Failed to load breakdown');
    } finally {
      setBreakdownLoading(false);
    }
  };

  const sideHint =
    breakdownSide === 'adp'
      ? 'ADP wage types on the register'
      : breakdownSide === 'both'
        ? 'Oracle elements and ADP wage types'
        : 'Oracle pay elements in element entries';

  return (
    <div className="space-y-2">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onApply();
        }}
        className="flex flex-wrap items-center justify-between gap-2"
      >
        <p className="text-xs text-slate-400">
          {hasPendingApply
            ? 'Press Apply to filter by person number or search.'
            : hasActiveFilters
              ? 'Filters applied.'
              : `Click a person number to see ${sideHint}.`}
        </p>
        <div className="flex flex-wrap gap-2">
          <button type="submit" className="btn-primary py-1.5 text-sm" disabled={loading}>
            Apply filters
          </button>
          {hasActiveFilters && (
            <button type="button" onClick={onClearFilters} className="btn-secondary py-1.5 text-sm">
              Clear all
            </button>
          )}
        </div>
      </form>
      <div className="card-panel overflow-x-auto">
        <table className="data-table text-sm">
          <thead>
            <tr>
              <th className="w-8 px-1 py-2" aria-hidden />
              <th className="px-2 py-2 font-medium">Person number (Global ID)</th>
            </tr>
            <tr className="bg-slate-800/50">
              <th className="px-1 py-1.5" />
              <th className="px-2 py-1.5">
                <input
                  value={filters.person}
                  onChange={(e) => set('person', e.target.value)}
                  placeholder="Person #"
                  className="filter-input"
                />
              </th>
            </tr>
            <tr className="bg-slate-900/50">
              <th className="px-1 py-1.5" />
              <th className="px-2 py-1.5">
                <input
                  value={filters.q}
                  onChange={(e) => set('q', e.target.value)}
                  placeholder="Search person — press Apply"
                  className="filter-input text-sm"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={2} className="px-2 py-8 text-center text-slate-400">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => {
                const open = expandedPerson === row.personNumber;
                return (
                  <tr key={`${row.personNumber}-${i}`} className="align-top">
                    <td colSpan={2} className="p-0">
                      <button
                        type="button"
                        onClick={() => togglePerson(row.personNumber)}
                        className="flex w-full items-center gap-2 px-2 py-2 text-left hover:bg-slate-800/60"
                      >
                        {open ? (
                          <ChevronDown className="h-4 w-4 shrink-0 text-cyan-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
                        )}
                        <span className="font-mono text-slate-200">{row.personNumber}</span>
                        <span className="text-xs text-slate-500">
                          {open ? 'Hide detail' : 'View elements / wage types'}
                        </span>
                      </button>
                      {open && (
                        <div className="border-t border-slate-700/80 bg-slate-900/40 px-4 py-3">
                          {breakdownLoading && (
                            <div className="flex items-center gap-2 py-2 text-xs text-slate-400">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Loading…
                            </div>
                          )}
                          {breakdownError && (
                            <p className="alert-error py-2 text-xs">{breakdownError}</p>
                          )}
                          {!breakdownLoading && !breakdownError && breakdown && (
                            <PersonBreakdownPanel data={breakdown} breakdownSide={breakdownSide} />
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
